























































































































import Vue from 'vue';
import {Logger} from '@/Shared/Logger';
import {Notification} from '@/Admin/NotificationManagement/Notification';
import VatsAPI from '@/Shared/VatsAPI';
import {AxiosResponse} from 'axios';
import {DateTimeFormatOptions} from 'vue-i18n';

const quickValidation = Vue.extend({
    data() {
        return {
            // clock stuff
            startDate: {} as Date,
            endDate: {} as Date,
            timeUpdateInterval: {} as number,
            numHoursToValidate: {} as number,

            // messagebox stuff
            modalTitle: '' as string,
            modalMessage: '' as string,

            // cosmetic stuff
            locationWelcomeMessage: {} as string,
            isBusy: false as boolean,

            // Notification stuff
            locationLotID: null as string | null,
            plateNumber: null as string | null
        };
    },

    created() {
        if (!this.isNullOrWhitespace(this.$route.query.locationDisplayName as string)) {
            this.locationWelcomeMessage = `Welcome to ${this.$route.query.locationDisplayName}`;
        } else {
            // default
            this.locationWelcomeMessage = 'Welcome!';
        }

        const defaultNumHoursToValidate: number = 3;
        if (!this.isNullOrWhitespace(this.$route.query.numHoursToValidate as string)) {
            this.numHoursToValidate = this.tryParseInt(
                this.$route.query.numHoursToValidate as string,
                defaultNumHoursToValidate);
        } else {
            // default
            this.numHoursToValidate = defaultNumHoursToValidate;
        }

        this.locationLotID = (this.$route.query.locationLotID) as string | null;

        // immediately set the dates without waiting on setInterval to tick
        this.updateDates();

        // update the dates every second
        this.timeUpdateInterval = setInterval(() => {
            this.updateDates();
        }, 1000);
    },

    methods: {
        updateDates() {
            this.startDate = new Date();
            this.endDate = this.getEndDate(this.startDate, this.numHoursToValidate);
        },

        showModalError(title: string, message: string) {
            this.modalTitle = !title ? 'Error' : title;
            this.modalMessage = !message ? 'An error has occurred.' : message;

            this.$bvModal.show('bv-modal-error');
        },

        showModalMessage(title: string, message: string) {
            this.modalTitle = title;
            this.modalMessage = message;

            this.$bvModal.show('bv-modal-message');
        },

        formatDateTime(date?: number | Date | undefined, options?: DateTimeFormatOptions): string {
            // default options, used by the "valid until" message on screen.
            if (!options) {
                options = {
                    hour: 'numeric',
                    minute: 'numeric'
                };
            }

            // Concise way to format time according to system locale.
            return Intl.DateTimeFormat(navigator.language, options).format(date);
        },

        getEndDate(startDate: Date, numHoursToValidate: number): Date {
            // create the end date from the start date and add the expiration interval to it.
            let endDate = new Date(startDate);

            // tested at 11:50 PM; correctly returned "2021-10-07T06:50:45.237Z"
            // i.e., adding hours that sends the date past midnight correctly advances the calendar day
            endDate = new Date(endDate.setHours(endDate.getHours() + numHoursToValidate));

            return endDate;
        },

        isNullOrWhitespace(input: string | null | undefined) {
            return !input || input.trim().length < 1;
        },

        tryParseInt(input: string | null | undefined, defaultValue: number, radix: number = 10): number {
            const value: number = Number(input);
            return isNaN(value) ? defaultValue : value;
        },

        async createNotificationForPlate() {

            if (this.isNullOrWhitespace(this.plateNumber)) {
                this.showModalError('Invalid Plate', 'Please enter a valid plate number.');
                return;
            }

            try {
                // show busy
                this.isBusy = true;

                // do the thing
                Logger.Log(`Adding VATS notification for vehicle plate '${this.plateNumber}' from location '${this.locationWelcomeMessage}' as lotId '${this.locationLotID}'`);

                const result: AxiosResponse<any> | void = await this.writeNotificationToVATS();

                if (!result || result.statusText !== 'OK') {
                    // noinspection ExceptionCaughtLocallyJS
                    throw new Error();
                }

                // report success
                Logger.Log(`Created notification with result '${result.statusText}'`);

                this.showModalMessage('Thank you!', `Plate ${this.plateNumber} is validated until ${this.formatDateTime(this.endDate)}.`);
            } catch (e) {
                Logger.Log(e);

                // report the problem to the user
                this.showModalError('Error validating plate', 'Error while validating vehicle. NOTE: Only one validation permitted per day.');
            } finally {
                // clear the textbox for the next entry
                this.plateNumber = '';

                // stop showing busy
                this.isBusy = false;
            }
        },
        async writeNotificationToVATS(): Promise<AxiosResponse<any> | void> {
            // we don't need to read LocationDBName because it's always Merrick Park.
            // in fact, in testing, i keep getting "vatsDBMobile" by default because it's
            // where i happen to be logged in.  it won't hurt anything if it's missing or wrong
            // since we ignore it, but it's irritating behavior.

            const n = {} as Notification;
            n.lotId = this.locationLotID ?? '';
            n.vehicleLicense = this.plateNumber!;
            n.startDate = this.startDate;
            n.endDate = this.endDate;
            n.noteCodeDescription = 'Permit';
            // TODO: confirm note text
            n.noteText = `Permit expires ${(this.formatDateTime(this.endDate, {
                hour12: true,
                month: 'short',
                day: 'numeric',
                year: 'numeric',
                hour: 'numeric',
                minute: 'numeric'
            } as DateTimeFormatOptions))}`;

            // gross hard coding of the location (Merrick Park)
            // ignores the value (if any) of the query parameter LocationDBName
            const url = `MerrickPark/QuickValidation/AddNotification`;

            return await VatsAPI.post(url, n)
                .catch((e) => {
                    // uncaught, a post error sent us to the login page.  we have to stay here!
                    Logger.Log(`POST error: '${e}'`);
                });
        }
    },

    beforeDestroy() {
        // prevent memory leak
        clearInterval(this.timeUpdateInterval);
    }
});

export default quickValidation;
